import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { api, errMsg, fmtTime, todayISO } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

const TILES = [
  ["present", "Present", "text-emerald-400"],
  ["late", "Late", "text-amber-400"],
  ["absent", "Absent", "text-rose-400"],
  ["off_days", "Weekly off", "text-indigo-400"],
  ["holidays", "Holidays", "text-sky-400"],
  ["sick_leave", "Sick leave", "text-fuchsia-400"],
  ["hours", "Hours", "text-white"],
];

export default function History() {
  const [code, setCode] = useState("");
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (m = month) => {
    if (!code.trim()) return;
    setBusy(true); setError("");
    try { const r = await api.post("/public/history", { code, month: m }); setData(r.data); }
    catch (e) { setData(null); setError(errMsg(e)); } finally { setBusy(false); }
  };

  const changeMonth = (m) => { if (!m) return; setMonth(m); if (data) load(m); };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 py-8 lg:py-12 space-y-8">
        <Link to="/" data-testid="history-back-to-kiosk" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft size={14} /> Back to kiosk</Link>
        <div className="rise">
          <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-400 uppercase">Self service</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">My Attendance</h1>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-col sm:flex-row gap-3 bg-slate-900 border border-slate-800 rounded-2xl p-4">
          <input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Your worker code" data-testid="history-code-input" className="flex-1 h-12 bg-slate-950 border-2 border-slate-800 focus:border-emerald-500 outline-none rounded-xl px-4 font-mono text-xl tracking-[0.2em] transition-colors" />
          <input type="month" value={month} onChange={(e) => changeMonth(e.target.value)} data-testid="history-month-input" className="h-12 bg-slate-950 border-2 border-slate-800 rounded-xl px-3 font-mono [color-scheme:dark]" />
          <button disabled={busy || !code} data-testid="history-submit-btn" className="h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold flex items-center justify-center gap-2 transition-colors">
            <Search size={16} /> {busy ? "Loading…" : "View"}
          </button>
        </form>
        {error && <p data-testid="history-error" className="text-rose-400">{error}</p>}

        {data && <HistoryResult data={data} />}
      </div>
    </div>
  );
}

const HistoryResult = ({ data }) => (
  <div className="space-y-6 rise">
    <div>
      <h2 data-testid="history-worker-name" className="font-display text-2xl font-bold">{data.worker.name}</h2>
      <p className="font-mono text-sm text-slate-400">{data.worker.code} · {data.worker.department || "—"} · Shift {data.worker.shift_start}–{data.worker.shift_end} · Off {data.worker.weekly_off_day}</p>
    </div>
    <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
      {TILES.map(([k, label, tone]) => (
        <div key={k} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-500">{label}</p>
          <p data-testid={`history-total-${k}`} className={`font-mono text-3xl font-bold mt-1 ${tone}`}>{data.totals[k]}</p>
        </div>
      ))}
    </div>
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="history-days-table">
        <thead className="text-left text-xs uppercase text-slate-500 font-mono">
          <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">In</th><th className="px-4 py-3">Out</th><th className="px-4 py-3">Hours</th></tr>
        </thead>
        <tbody>
          {data.days.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No days to show for this month.</td></tr>}
          {data.days.map((d) => (
            <tr key={d.date} className="border-t border-slate-800" data-testid={`history-day-${d.date}`}>
              <td className="px-4 py-3"><p className="font-mono">{d.date}</p><p className="text-xs text-slate-500">{d.weekday}{d.holiday ? ` · ${d.holiday}` : ""}</p></td>
              <td className="px-4 py-3"><StatusBadge status={d.status} />{d.late_minutes > 0 && <span className="ml-2 text-xs text-amber-400 font-mono">+{d.late_minutes}m</span>}</td>
              <td className="px-4 py-3 font-mono text-slate-300">{d.worked ? fmtTime(d.check_in) : "—"}</td>
              <td className="px-4 py-3 font-mono text-slate-300">{d.worked ? fmtTime(d.check_out) : "—"}</td>
              <td className="px-4 py-3 font-mono text-slate-300">{d.check_out ? d.hours : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
